/**
 * Price Alerts Page - Pro Only
 * Create and manage custom price target alerts with email notifications
 */
import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import {
  Bell, BellRing, Plus, Trash2, TrendingUp, TrendingDown,
  Lock, Crown, Loader2, Search, ArrowUp, ArrowDown,
  Mail, AlertTriangle, Check, X
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AlertsPage = () => {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [error, setError] = useState(null);
  
  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedInstrument, setSelectedInstrument] = useState(null);
  
  // New alert form
  const [newAlert, setNewAlert] = useState({
    symbol: '',
    target_price: '',
    condition: 'above',
    note: ''
  });

  const fetchAlerts = useCallback(async () => {
    if (!token) return;
    
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/alerts/my-alerts?include_triggered=true`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.status === 403) {
        setError('pro_required');
        return;
      }
      
      if (response.ok) {
        const data = await response.json();
        setAlerts(data.alerts || []);
      }
    } catch (err) {
      console.error('Error fetching alerts:', err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchAlerts();
  }, [fetchAlerts]);

  const searchInstruments = async (query) => {
    if (!query || query.length < 2) {
      setSearchResults([]);
      return;
    }
    
    setSearching(true);
    try {
      const response = await fetch(`${API_URL}/api/instruments/search?q=${encodeURIComponent(query)}&limit=5`, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      
      if (response.ok) {
        const data = await response.json();
        setSearchResults(data || []);
      }
    } catch (err) {
      console.error('Search error:', err);
    } finally {
      setSearching(false);
    }
  };

  useEffect(() => {
    const debounce = setTimeout(() => {
      searchInstruments(searchQuery);
    }, 300);
    return () => clearTimeout(debounce);
  }, [searchQuery]);

  const selectInstrument = (instrument) => {
    setSelectedInstrument(instrument);
    setNewAlert({
      ...newAlert,
      symbol: instrument.symbol,
      target_price: ''
    });
    setSearchQuery('');
    setSearchResults([]);
  };

  const createAlert = async () => {
    if (!newAlert.symbol || !newAlert.target_price) {
      toast.error('Please select an instrument and enter a target price');
      return;
    }
    
    setCreating(true);
    try {
      const response = await fetch(`${API_URL}/api/alerts/create`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          symbol: newAlert.symbol,
          target_price: parseFloat(newAlert.target_price),
          condition: newAlert.condition,
          note: newAlert.note || null
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(data.message || 'Alert created!');
        setShowCreate(false);
        setNewAlert({ symbol: '', target_price: '', condition: 'above', note: '' });
        setSelectedInstrument(null);
        fetchAlerts();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to create alert');
      }
    } catch (err) {
      toast.error('Failed to create alert');
    } finally {
      setCreating(false);
    }
  };

  const deleteAlert = async (alertId) => {
    try {
      const response = await fetch(`${API_URL}/api/alerts/${alertId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast.success('Alert deleted');
        fetchAlerts();
      } else {
        toast.error('Failed to delete alert');
      }
    } catch (err) {
      toast.error('Failed to delete alert');
    }
  };

  // Pro upgrade prompt for non-Pro users
  if (error === 'pro_required' || (user && !user.is_pro)) {
    return (
      <div className="min-h-screen bg-slate-950 py-12">
        <div className="max-w-2xl mx-auto px-4">
          <Card className="bg-gradient-to-br from-purple-900/30 to-slate-900/50 border-purple-500/30">
            <CardContent className="p-8 text-center">
              <div className="w-16 h-16 bg-purple-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Lock className="h-8 w-8 text-purple-400" />
              </div>
              <h1 className="text-2xl font-bold text-white mb-2">Pro Feature</h1>
              <p className="text-slate-400 mb-6">
                Price Alerts is available exclusively for Pro members.
                Get notified via email when prices hit your targets.
              </p>
              <div className="grid grid-cols-2 gap-4 mb-6 text-left">
                <div className="bg-slate-800/50 rounded-lg p-3">
                  <Bell className="h-5 w-5 text-emerald-400 mb-2" />
                  <p className="text-white text-sm font-medium">Custom Alerts</p>
                  <p className="text-slate-400 text-xs">Set price targets</p>
                </div>
                <div className="bg-slate-800/50 rounded-lg p-3">
                  <Mail className="h-5 w-5 text-blue-400 mb-2" />
                  <p className="text-white text-sm font-medium">Email Notifications</p>
                  <p className="text-slate-400 text-xs">Never miss a move</p>
                </div>
                <div className="bg-slate-800/50 rounded-lg p-3">
                  <ArrowUp className="h-5 w-5 text-emerald-400 mb-2" />
                  <p className="text-white text-sm font-medium">Above Alerts</p>
                  <p className="text-slate-400 text-xs">When price rises</p>
                </div>
                <div className="bg-slate-800/50 rounded-lg p-3">
                  <ArrowDown className="h-5 w-5 text-red-400 mb-2" />
                  <p className="text-white text-sm font-medium">Below Alerts</p>
                  <p className="text-slate-400 text-xs">When price drops</p>
                </div>
              </div>
              <Button 
                onClick={() => navigate('/subscription')}
                className="bg-purple-600 hover:bg-purple-500"
              >
                <Crown className="h-4 w-4 mr-2" />
                Upgrade to Pro
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <Loader2 className="h-8 w-8 text-purple-500 animate-spin" />
      </div>
    );
  }

  const activeAlerts = alerts.filter(a => a.is_active && !a.is_triggered);
  const triggeredAlerts = alerts.filter(a => a.is_triggered);

  return (
    <div className="min-h-screen bg-slate-950 py-8">
      <div className="max-w-4xl mx-auto px-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-3">
              <Bell className="h-7 w-7 text-purple-500" />
              Price Alerts
              <Badge className="bg-purple-500/20 text-purple-400">PRO</Badge>
            </h1>
            <p className="text-slate-400 mt-1">
              Get notified when prices hit your targets
            </p>
          </div>
          <Button 
            onClick={() => setShowCreate(!showCreate)}
            className="bg-emerald-600 hover:bg-emerald-500"
            disabled={activeAlerts.length >= 10}
          >
            <Plus className="h-4 w-4 mr-2" />
            New Alert
          </Button>
        </div>

        {/* Alert Limit Warning */}
        {activeAlerts.length >= 8 && (
          <div className="mb-4 p-3 bg-yellow-900/20 border border-yellow-700/30 rounded-lg flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-yellow-400" />
            <span className="text-yellow-400 text-sm">
              {activeAlerts.length}/10 active alerts used
            </span>
          </div>
        )}

        {/* Create Alert Form */}
        {showCreate && (
          <Card className="bg-slate-900/50 border-slate-800 mb-6">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg text-white">Create New Alert</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Instrument Search */}
              <div>
                <label className="text-sm text-slate-400 block mb-2">Search Instrument</label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search by symbol or name..."
                    className="pl-10 bg-slate-800 border-slate-700"
                  />
                  {searching && (
                    <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 animate-spin" />
                  )}
                </div>
                
                {/* Search Results */}
                {searchResults.length > 0 && (
                  <div className="mt-2 bg-slate-800 rounded-lg border border-slate-700 overflow-hidden">
                    {searchResults.map((instrument) => (
                      <button
                        key={instrument.symbol}
                        onClick={() => selectInstrument(instrument)}
                        className="w-full px-4 py-3 flex items-center justify-between hover:bg-slate-700/50 transition-colors text-left"
                      >
                        <div>
                          <span className="text-white font-medium">{instrument.symbol}</span>
                          <span className="text-slate-400 text-sm ml-2">{instrument.name}</span>
                        </div>
                        <span className="text-emerald-400">${instrument.current_price_vd?.toFixed(2)}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Selected Instrument */}
              {selectedInstrument && (
                <div className="p-3 bg-slate-800/50 rounded-lg border border-slate-700">
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="text-white font-medium">{selectedInstrument.symbol}</span>
                      <span className="text-slate-400 text-sm ml-2">{selectedInstrument.name}</span>
                    </div>
                    <div className="text-right">
                      <p className="text-emerald-400 font-bold">${selectedInstrument.current_price_vd?.toFixed(2)}</p>
                      <p className="text-slate-400 text-xs">Current Price</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Alert Condition */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-slate-400 block mb-2">Condition</label>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant={newAlert.condition === 'above' ? 'default' : 'outline'}
                      onClick={() => setNewAlert({ ...newAlert, condition: 'above' })}
                      className={newAlert.condition === 'above' 
                        ? 'bg-emerald-600 hover:bg-emerald-500 flex-1' 
                        : 'border-slate-700 flex-1'
                      }
                    >
                      <ArrowUp className="h-4 w-4 mr-1" />
                      Above
                    </Button>
                    <Button
                      type="button"
                      variant={newAlert.condition === 'below' ? 'default' : 'outline'}
                      onClick={() => setNewAlert({ ...newAlert, condition: 'below' })}
                      className={newAlert.condition === 'below' 
                        ? 'bg-red-600 hover:bg-red-500 flex-1' 
                        : 'border-slate-700 flex-1'
                      }
                    >
                      <ArrowDown className="h-4 w-4 mr-1" />
                      Below
                    </Button>
                  </div>
                </div>
                <div>
                  <label className="text-sm text-slate-400 block mb-2">Target Price ($)</label>
                  <Input
                    type="number"
                    step="0.01"
                    value={newAlert.target_price}
                    onChange={(e) => setNewAlert({ ...newAlert, target_price: e.target.value })}
                    placeholder="0.00"
                    className="bg-slate-800 border-slate-700"
                  />
                </div>
              </div>

              {/* Note */}
              <div>
                <label className="text-sm text-slate-400 block mb-2">Note (optional)</label>
                <Input
                  value={newAlert.note}
                  onChange={(e) => setNewAlert({ ...newAlert, note: e.target.value })}
                  placeholder="e.g., Sell trigger, Buy opportunity..."
                  className="bg-slate-800 border-slate-700"
                />
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowCreate(false);
                    setSelectedInstrument(null);
                    setNewAlert({ symbol: '', target_price: '', condition: 'above', note: '' });
                  }}
                  className="border-slate-700"
                >
                  Cancel
                </Button>
                <Button
                  onClick={createAlert}
                  disabled={creating || !selectedInstrument || !newAlert.target_price}
                  className="bg-emerald-600 hover:bg-emerald-500"
                >
                  {creating ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Bell className="h-4 w-4 mr-2" />}
                  Create Alert
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Active Alerts */}
        <Card className="bg-slate-900/50 border-slate-800 mb-6">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg text-white flex items-center gap-2">
              <BellRing className="h-5 w-5 text-emerald-400" />
              Active Alerts
              <Badge className="bg-slate-700 text-slate-300">{activeAlerts.length}/10</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {activeAlerts.length > 0 ? (
              <div className="space-y-3">
                {activeAlerts.map((alert) => (
                  <div 
                    key={alert.id}
                    className="p-4 bg-slate-800/50 rounded-lg border border-slate-700 flex items-center justify-between"
                    data-testid={`alert-${alert.id}`}
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-3">
                        <span className="text-white font-bold">{alert.symbol}</span>
                        <Badge className={alert.condition === 'above' 
                          ? 'bg-emerald-500/20 text-emerald-400' 
                          : 'bg-red-500/20 text-red-400'
                        }>
                          {alert.condition === 'above' ? (
                            <><ArrowUp className="h-3 w-3 mr-1" /> Above</>
                          ) : (
                            <><ArrowDown className="h-3 w-3 mr-1" /> Below</>
                          )}
                        </Badge>
                        <span className="text-yellow-400 font-bold">${alert.target_price?.toFixed(2)}</span>
                      </div>
                      <div className="flex items-center gap-4 mt-2 text-sm">
                        <span className="text-slate-400">
                          Current: <span className="text-white">${alert.current_price?.toFixed(2)}</span>
                        </span>
                        <span className={`${alert.distance_percent >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                          {alert.distance_percent >= 0 ? '+' : ''}{alert.distance_percent?.toFixed(2)}% away
                        </span>
                      </div>
                      {alert.note && (
                        <p className="text-slate-500 text-xs mt-1 italic">"{alert.note}"</p>
                      )}
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => deleteAlert(alert.id)}
                      className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-slate-400">
                <Bell className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p>No active alerts</p>
                <p className="text-sm">Create an alert to get notified when prices move</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Triggered Alerts */}
        {triggeredAlerts.length > 0 && (
          <Card className="bg-slate-900/50 border-slate-800">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg text-white flex items-center gap-2">
                <Check className="h-5 w-5 text-yellow-400" />
                Triggered Alerts
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {triggeredAlerts.slice(0, 5).map((alert) => (
                  <div 
                    key={alert.id}
                    className="p-3 bg-slate-800/30 rounded-lg border border-slate-700/50 opacity-75"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <span className="text-white font-medium">{alert.symbol}</span>
                        <Badge className="bg-yellow-500/20 text-yellow-400">
                          <Check className="h-3 w-3 mr-1" />
                          Triggered
                        </Badge>
                      </div>
                      <div className="text-right text-sm">
                        <p className="text-slate-400">Target: ${alert.target_price?.toFixed(2)}</p>
                        <p className="text-yellow-400">Hit at: ${alert.triggered_price?.toFixed(2)}</p>
                      </div>
                    </div>
                    {alert.email_sent && (
                      <p className="text-xs text-slate-500 mt-2 flex items-center gap-1">
                        <Mail className="h-3 w-3" /> Email sent
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default AlertsPage;
